import { forwardRef, useState } from 'react'
import { Modal, Platform, Pressable, StyleSheet, View } from 'react-native'
import { IconButton, Text, useTheme } from 'react-native-paper'
import { Button } from '@/ui/Button'

interface PeriodNavigatorProps {
    periodLabel: string
    onPrevious: () => void
    onNext: () => void
    periodDate?: Date
    onDatePicked?: (date: Date) => void
}

export function PeriodNavigator({
    periodLabel,
    onPrevious,
    onNext,
    periodDate,
    onDatePicked,
}: PeriodNavigatorProps) {
    const [showDatePicker, setShowDatePicker] = useState(false)

    const hasDatePicker = !!periodDate && !!onDatePicked

    const handleLabelPress = () => {
        if (hasDatePicker) {
            setShowDatePicker(true)
        }
    }

    const handleDateChange = (date: Date | null) => {
        if (date && onDatePicked) {
            onDatePicked(date)
        }
        setShowDatePicker(false)
    }

    return (
        <View style={styles.container}>
            <View style={styles.navigation}>
                <IconButton icon="chevron-left" size={20} onPress={onPrevious} />
                <View style={styles.labelContainer}>
                    <Pressable onPress={handleLabelPress} disabled={!hasDatePicker}>
                        <Text variant="titleMedium" style={styles.label}>
                            {periodLabel}
                        </Text>
                    </Pressable>
                    {hasDatePicker && Platform.OS !== 'web' && (
                        <IconButton
                            icon="calendar"
                            size={16}
                            onPress={() => setShowDatePicker(true)}
                            style={styles.iconButton}
                        />
                    )}
                    {hasDatePicker && Platform.OS === 'web' && (
                        <WebDatePicker
                            value={periodDate}
                            onChange={handleDateChange}
                            isOpen={showDatePicker}
                            onOpen={() => setShowDatePicker(true)}
                            onClose={() => setShowDatePicker(false)}
                        />
                    )}
                </View>
                <IconButton icon="chevron-right" size={20} onPress={onNext} />
            </View>
            {hasDatePicker && showDatePicker && Platform.OS !== 'web' && (
                <NativeDatePicker
                    visible={showDatePicker}
                    value={periodDate}
                    onChange={handleDateChange}
                    onDismiss={() => setShowDatePicker(false)}
                />
            )}
        </View>
    )
}

interface NativeDatePickerProps {
    visible: boolean
    value: Date
    onChange: (date: Date | null) => void
    onDismiss: () => void
}

function NativeDatePicker({ visible, value, onChange, onDismiss }: NativeDatePickerProps) {
    const theme = useTheme()

    // Lazy import to avoid loading native module on web
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const RNDateTimePicker = require('@react-native-community/datetimepicker').default

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onDismiss}>
            <Pressable style={styles.modalOverlay} onPress={onDismiss}>
                <View
                    style={[styles.modalContent, { backgroundColor: theme.colors.surface }]}
                    onStartShouldSetResponder={() => true}
                >
                    <RNDateTimePicker
                        value={value}
                        mode="date"
                        display="inline"
                        maximumDate={new Date()}
                        onChange={(_event: unknown, date?: Date) => {
                            if (date) onChange(date)
                        }}
                    />
                    <Button title="Close" onPress={onDismiss} />
                </View>
            </Pressable>
        </Modal>
    )
}

interface WebDatePickerProps {
    value: Date
    onChange: (date: Date | null) => void
    isOpen: boolean
    onOpen: () => void
    onClose: () => void
}

const CalendarIconInput = forwardRef<HTMLButtonElement, { onClick?: () => void }>(
    ({ onClick }, ref) => (
        <IconButton
            ref={ref as React.Ref<View>}
            icon="calendar"
            size={16}
            onPress={onClick}
            style={styles.iconButton}
        />
    )
)
CalendarIconInput.displayName = 'CalendarIconInput'

function WebDatePicker({ value, onChange, isOpen, onOpen, onClose }: WebDatePickerProps) {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const ReactDatePicker = require('react-datepicker').default

    return (
        <ReactDatePicker
            selected={value}
            onChange={(date: Date | null) => {
                if (date) onChange(date)
            }}
            maxDate={new Date()}
            open={isOpen}
            onInputClick={onOpen}
            onClickOutside={onClose}
            portalId="datepicker-portal"
            customInput={<CalendarIconInput />}
        />
    )
}

const styles = StyleSheet.create({
    container: {
        paddingVertical: 8,
        paddingHorizontal: 4,
    },
    navigation: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
    },
    labelContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    label: {
        fontWeight: '600',
    },
    iconButton: {
        margin: 0,
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalContent: {
        borderRadius: 16,
        padding: 16,
        elevation: 8,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
    },
})
